<template>
  <div class="import-url-group-modal">
    <div
      v-if="csvLoaded && !config.item.showDownloadReport && !csvLoadError"
      class="d-flex w-100 flex-column align-center text-center"
    >
      <v-icon size="70" class="mb-6 success-icon" icon="$allGood" />
      <div class="headline6 text-center px-6">
        {{ $t(`modals.${config.action}.importInProgress`) }}
      </div>
      <v-btn
        data-testid="got-it-button"
        color="primary"
        class="mt-7"
        rounded
        size="large"
        @click="$emit('dismissed')"
      >
        {{ $t("general.gotIt") }}
      </v-btn>
    </div>
    <div v-else-if="csvLoading" class="info-section d-flex w-100 flex-column justify-space-between">
      <div class="csv-upload-report d-flex w-100 pl-4 pt-4 pr-4 pb-4">
        <v-icon class="file-icon mr-2" size="70" icon="$dataNoHexagon" />
        <div class="d-flex flex-column align-start justify-center w-100">
          <div class="subtitle2">{{ file?.name }}</div>
          <div class="body3 text-indigo-medium mb-2">{{ humanizeFileSize(file?.size) }}</div>
          <v-progress-linear
            color="green-base"
            bg-color="indigo-pale"
            rounded
            height="4"
            indeterminate
          />
        </div>
      </div>
      <v-btn
        rounded
        class="close-btn align-self-end"
        color="primary"
        data-testid="modal-cancel-button"
        size="large"
        @click="$emit('dismissed')"
      >
        {{ $t("general.cancel") }}
      </v-btn>
    </div>
    <div
      v-else-if="csvLoadError || invalidRecords"
      class="info-section d-flex w-100 flex-column justify-space-between"
    >
      <div class="d-flex flex-column ga-4">
        <div class="csv-upload-report error-border-solid d-flex w-100 pl-4 pt-4 pr-4 pb-4">
          <v-icon class="file-icon mr-2" size="70" icon="$dataNoHexagon" />
          <div class="d-flex flex-column align-start">
            <div class="subtitle2">{{ file?.name }}</div>
            <div class="body3 text-indigo-medium mb-2">{{ humanizeFileSize(file?.size) }}</div>
            <div class="d-flex align-center">
              <v-icon icon="$warning" color="red-faint" size="18" class="mr-1" />
              <span class="body2 text-red-dark">
                <template v-if="csvLoadError">
                  {{ csvErrorMessage || $t(`modals.${config.action}.errors.general`) }}
                </template>
                <template v-else>
                  {{
                    $t(`modals.${config.action}.invalidRecords`, {
                      n: invalidRecords,
                      sum: invalidRecords + successfullyUploaded,
                    })
                  }}
                </template>
              </span>
              <a data-testid="retry-link" class="coro-link pl-2" @click="handleFileImport()">
                {{ $t("general.tryAgain") }}
              </a>
            </div>
          </div>
        </div>
        <div v-if="invalidRecords" class="body3">
          <a data-testid="download-template-link" class="coro-link" @click="downloadTemplate()">{{
            $t(`modals.${config.action}.downloadTemplate`)
          }}</a>
          {{ $t(`modals.${config.action}.disclaimer`) }}
        </div>
      </div>
      <v-btn
        rounded
        class="close-btn align-self-end"
        color="primary"
        data-testid="modal-action-button"
        size="large"
        @click="$emit('dismissed')"
      >
        {{ $t("general.close") }}
      </v-btn>
    </div>

    <!-- Success state — all records imported, no errors -->
    <div v-else-if="csvLoaded" class="info-section d-flex w-100 flex-column justify-space-between">
      <div class="csv-upload-report d-flex w-100 pl-4 pt-4 pr-4 pb-4">
        <v-icon class="file-icon mr-2" size="70" icon="$dataNoHexagon" />
        <div class="d-flex flex-column align-start">
          <div class="subtitle2">{{ file?.name }}</div>
          <div class="body3 text-indigo-medium mb-2">{{ humanizeFileSize(file?.size) }}</div>
          <div class="d-flex align-center">
            <v-icon size="24" icon="$circleCheck" class="mr-2" color="green-dark" />
            <span class="body2 text-green-dark">
              {{ $t(`modals.${config.action}.successfullyUploaded`, { n: successfullyUploaded }) }}
            </span>
          </div>
        </div>
      </div>
      <v-btn
        rounded
        class="close-btn align-self-end"
        color="primary"
        data-testid="modal-action-button"
        size="large"
        @click="$emit('dismissed')"
      >
        {{ $t("general.close") }}
      </v-btn>
    </div>
    <template v-else>
      <v-form ref="form" v-model="valid" validate-on="input lazy" @submit.prevent>
        <draggable-file-uploader
          :description="$t(`modals.${config.action}.uploadTxt`)"
          :subtitle="
            $t(`modals.${config.action}.subtitle`, {
              size: config.item.maxFileSize ? humanizeFileSize(config.item.maxFileSize) : '1 MB',
            })
          "
          :class="{ 'error-border': !!errorMessage }"
          :error-message="errorMessage"
          @file-changed="handleFileUpload($event)"
        />
        <div class="body3 mt-2">
          <a data-testid="download-template-link" class="coro-link" @click="downloadTemplate()">{{
            $t(`modals.${config.action}.downloadTemplate`)
          }}</a>
          {{ $t(`modals.${config.action}.disclaimer`) }}
        </div>
        <div class="d-flex justify-end mt-5 mb-3">
          <v-btn
            data-testid="modal-cancel-button"
            color="primary"
            :ripple="false"
            variant="text"
            @click="$emit('dismissed')"
          >
            {{ $t("general.cancel") }}
          </v-btn>
          <v-btn
            rounded
            color="primary"
            data-testid="modal-action-button"
            size="large"
            :disabled="disable"
            @click="handleFileImport()"
          >
            {{ $t(`modals.${config.action}.actionBtn`) }}
          </v-btn>
        </div>
      </v-form>
    </template>
  </div>
</template>

<script lang="ts">
import DraggableFileUploader from "@/components/DraggableFileUploader.vue";
import { computed, defineComponent, type PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useImportCsvModal } from "@/composables/useImportCsvModal";
import { DataSizeUnit } from "@/constants/general";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { humanizeFileSize } from "@coronet/cui";

export default defineComponent({
  components: {
    DraggableFileUploader,
  },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig>,
      required: true,
    },
    disable: {
      type: Boolean,
      required: false,
    },
  },
  emits: ["dismissed"],
  setup(props) {
    const valid = ref(true);
    const form = ref();
    const isFormSubmitted = ref(false);

    const i18n = useI18n();

    const {
      file,
      csvLoaded,
      csvLoading,
      csvLoadError,
      handleFileUpload,
      handleImport,
      invalidRecords,
      successfullyUploaded,
      errorMessage: csvErrorMessage,
    } = useImportCsvModal();

    const errorMessage = computed(() => {
      if (file.value && file.value.size > (props.config.item.maxFileSize || DataSizeUnit.MB)) {
        return i18n.t("validations.fileSizeError");
      }
      if (isFormSubmitted.value && !file.value) {
        return i18n.t("validations.missingFile");
      }
      return "";
    });

    const handleFileImport = async () => {
      isFormSubmitted.value = true;
      if (!file.value || !!errorMessage.value) {
        return;
      }

      await handleImport(file.value, props.config.item.uploadCallback);
    };

    const downloadTemplate = async () => {
      await props.config.item.downloadTemplateCallback();
    };

    return {
      form,
      valid,
      file,
      csvLoaded,
      csvLoading,
      csvLoadError,
      errorMessage,
      csvErrorMessage,
      handleFileUpload,
      handleFileImport,
      downloadTemplate,
      invalidRecords,
      successfullyUploaded,
      humanizeFileSize,
    };
  },
});
</script>

<style lang="scss" scoped>
.import-url-group-modal,
.info-section {
  min-height: 300px;
}

.success-icon:before {
  color: rgb(var(--v-theme-green-base));
}

.error-border {
  border: dashed 1px rgb(var(--v-theme-red-dark));
}

.csv-upload-report {
  border: solid 1px rgb(var(--v-theme-indigo-pale));
  border-radius: 8px;

  &.error-border-solid {
    border: solid 2px rgb(var(--v-theme-red-dark));
  }
}

.file-icon {
  background-color: rgb(var(--v-theme-indigo-faint));
  border-radius: 8px;
}

.close-btn {
  margin-bottom: 24px;
}
</style>
